import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { api } from '../api';
import Header from '../components/Header';
import {
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  TextField,
  Button,
  Toolbar,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { HomeIcon, SettingsIcon, PhotoCameraIcon, DeleteIcon } from '../components/MuiIcons';
import EmojiEventsIcon from '../components/EmojiEventsIcon';
import ListItemButton from '@mui/material/ListItemButton';

const drawerWidth = 220;

const MyAccountPage: React.FC = () => {
  const history = useHistory();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const stored = localStorage.getItem('user');
  const user = stored ? JSON.parse(stored) : null;

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        history.push('/signin');
        return;
      }

      try {
        const res = await fetch(api(`/api/auth/${user.id}/profile`));
        const data = await res.json();
        if (res.ok) {
          setName(data.name || '');
          setEmail(data.email || '');
          setAvatarUrl(data.avatarUrl ? (process.env.REACT_APP_API_URL || '') + data.avatarUrl : null);
          
          // Update localStorage with latest data from database
          const updatedUser = {
            ...user,
            name: data.name,
            email: data.email,
            avatarUrl: data.avatarUrl ? (process.env.REACT_APP_API_URL || '') + data.avatarUrl : null
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user?.id, history]);

  const handleMenuItemClick = (path: string) => {
    history.push(path);
    setDrawerOpen(false);
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const res = await fetch(api(`/api/auth/${user.id}/avatar`), {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        const fullAvatarUrl = (process.env.REACT_APP_API_URL || '') + data.avatarUrl;
        
        setAvatarUrl(fullAvatarUrl);
        
        const updatedUser = { ...user, avatarUrl: fullAvatarUrl };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setSuccess('Profile picture updated successfully!');
        setTimeout(() => {
          setSuccess('');
          window.location.reload();
        }, 1500);
      } else {
        setError(data.error || 'Failed to upload avatar');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.id) return;

    setRemoving(true);
    setError('');

    try {
      const res = await fetch(api(`/api/auth/${user.id}/avatar`), {
        method: 'DELETE',
      });

      const data = await res.json();

      if (res.ok) {
        setAvatarUrl(null);
        
        const updatedUser = { ...user, avatarUrl: null };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setSuccess('Profile picture removed successfully!');
        setRemoveDialogOpen(false);
        
        setTimeout(() => {
          setSuccess('');
          window.location.reload();
        }, 1500);
      } else {
        setError(data.error || 'Failed to remove avatar');
        setRemoveDialogOpen(false);
      }
    } catch (error) {
      console.error('Remove error:', error);
      setError('Failed to remove avatar');
      setRemoveDialogOpen(false);
    } finally {
      setRemoving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(api(`/api/auth/${user.id}/profile`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }), // Only send name, not email
      });

      const data = await res.json();

      if (res.ok) {
        const updatedUser = { 
          ...user, 
          name: data.name, 
          email: data.email,
          avatarUrl: data.avatarUrl ? (process.env.REACT_APP_API_URL || '') + data.avatarUrl : user.avatarUrl
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setSuccess('Profile updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Update error:', error);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Header onMenuClick={() => setDrawerOpen(true)} title="My Account" />

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/home')}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/competitions')}>
              <ListItemIcon>
                <EmojiEventsIcon />
              </ListItemIcon>
              <ListItemText primary="Battles" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/settings')}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 4, mt: 8 }}>
        <Typography variant="h4" gutterBottom>
          My Account
        </Typography>

        <Paper sx={{ p: 3, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Profile Picture
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                key={avatarUrl || 'default'}
                src={avatarUrl || undefined}
                alt={name}
                sx={{ width: 100, height: 100 }}
              />
              {uploading && (
                <CircularProgress
                  size={100}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                />
              )}
            </Box>
            
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="avatar-upload"
                  type="file"
                  onChange={handleAvatarUpload}
                  disabled={uploading}
                />
                <label htmlFor="avatar-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<PhotoCameraIcon />}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                </label>
                
                {avatarUrl && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setRemoveDialogOpen(true)}
                    disabled={uploading || removing}
                  >
                    Remove Photo
                  </Button>
                )}
              </Box>
              <Typography variant="caption" display="block" color="text.secondary">
                Max file size: 5MB. Supported formats: JPG, PNG, GIF
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Profile Information
          </Typography>
          
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />
          
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={email}
            disabled
            type="email"
            helperText="Email cannot be changed"
          />

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          {success && (
            <Typography color="success.main" sx={{ mt: 2 }}>
              {success}
            </Typography>
          )}

          <Button 
            variant="contained" 
            sx={{ mt: 2 }} 
            onClick={handleSaveProfile}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Paper>
      </Box>

      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Profile Picture?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove your profile picture?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)} disabled={removing}>
            Cancel
          </Button>
          <Button color="error" onClick={handleRemoveAvatar} disabled={removing}>
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyAccountPage;